import { useState, useEffect, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { beraknaSkiftersattning, beraknaRegistreradeTimmar, basPassNamn } from '../../lib/constants'
import { useAuth } from '../../hooks/useAuth'
import { BekraftaModal } from '../../components/BekraftaModal'

export function SapPage() {
  const { medarbetare: adminInfo } = useAuth()
  const [klarMap, setKlarMap] = useState({}) // medarbetare_id -> rad i sap_klar för vald månad
  const [klarFraga, setKlarFraga] = useState(null) // { id, namn } som väntar på bekräftelse
  const [angraFraga, setAngraFraga] = useState(null) // { id, namn } ångra klar
  const [klarFel, setKlarFel] = useState('')
  const [klarSkickar, setKlarSkickar] = useState(false)
  const [flik, setFlik] = useState('passandringar')
  const [valdManad, setValdManad] = useState(() => {
    const idag = new Date()
    return `${idag.getFullYear()}-${String(idag.getMonth() + 1).padStart(2, '0')}`
  })
  const [valdAvdelning, setValdAvdelning] = useState('Alla')
  const [andrade, setAndrade] = useState([])
  const [atfLista, setAtfLista] = useState([])
  const [semLista, setSemLista] = useState([])
  const [laddarSem, setLaddarSem] = useState(false)
  // Semesterår börjar 1/4 och slutar 31/3. Värdet är året då det börjar.
  const [semAr, setSemAr] = useState(() => {
    const idag = new Date()
    return idag.getMonth() >= 3 ? idag.getFullYear() : idag.getFullYear() - 1
  })
  const [loading, setLoading] = useState(false)

  const AVDELNINGAR = ['Alla', 'RN', 'LN MMÖ', 'LN NKP', 'DISP']

  // Håller koll på vilket val som gäller just nu, så att ett sent svar från en äldre
  // hämtning (t.ex. "Alla" innan avdelningen byttes) aldrig skriver över det nya.
  const aktuellNyckel = useRef('')
  aktuellNyckel.current = `${valdManad}|${valdAvdelning}|${semAr}`

  // Öppna på adminens egen avdelning (en gång), men låt henne sedan välja fritt
  const harValtEgen = useRef(false)
  useEffect(() => {
    if (harValtEgen.current || !adminInfo?.avdelning) return
    harValtEgen.current = true
    if (AVDELNINGAR.includes(adminInfo.avdelning)) setValdAvdelning(adminInfo.avdelning)
  }, [adminInfo?.avdelning])

  useEffect(() => { hamtaAndrade(); hamtaAtf() }, [valdManad, valdAvdelning])
  useEffect(() => { hamtaKlar() }, [valdManad])

  const manadNamnText = (() => {
    const [a, m] = valdManad.split('-').map(Number)
    return new Date(a, m - 1, 1).toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' })
  })()

  // Vilka medarbetare som redan markerats "Klar i SAP" för vald månad
  async function hamtaKlar() {
    const { data } = await supabase.from('sap_klar').select('*').eq('manad', valdManad)
    const karta = {}
    for (const r of data || []) karta[r.medarbetare_id] = r
    setKlarMap(karta)
  }

  // Ångra: ta bort märket och notisen i medarbetarens inkorg
  async function angraKlar(medId) {
    if (!medId) return
    setKlarSkickar(true)
    setKlarFel('')
    try {
      const { data: borta, error: notisFel } = await supabase.from('ansokningar').delete()
        .eq('typ', 'passandring_notis')
        .eq('passandring_fran', 'SAP klar')
        .eq('mottagare_id', medId)
        .eq('fran_datum', `${valdManad}-01`)
        .select('id')
      if (notisFel) throw notisFel
      const { error: klarFel2 } = await supabase.from('sap_klar').delete().eq('medarbetare_id', medId).eq('manad', valdManad)
      if (klarFel2) throw klarFel2
      if (!borta || borta.length === 0) {
        setKlarFel('Märket är borttaget, men notisen hos medarbetaren kunde inte raderas (den kan redan vara borta, eller saknas behörighet).')
      }
      await hamtaKlar()
    } catch (err) {
      console.error('Ångra klar i SAP:', err)
      setKlarFel('Kunde inte ångra: ' + (err?.message || 'okänt fel'))
    } finally {
      setKlarSkickar(false)
    }
  }

  // Markera klar + skicka notis till medarbetarens inkorg
  async function skickaKlar(medId) {
    if (!medId || !adminInfo || klarMap[medId]) return
    setKlarSkickar(true)
    setKlarFel('')
    try {
      const adminNamn = `${adminInfo.fornamn} ${adminInfo.efternamn}`
      const nu = new Date().toISOString()
      // Först märkningen (primärnyckeln stoppar dubbletter), sedan notisen
      const { error: klarError } = await supabase.from('sap_klar').insert({
        medarbetare_id: medId, manad: valdManad, skickad_av: adminInfo.id, skickad_av_namn: adminNamn, skickad_at: nu
      })
      if (klarError) throw klarError

      const forstaDag = `${valdManad}-01`
      const { error: notisError } = await supabase.from('ansokningar').insert({
        typ: 'passandring_notis',
        skapad_av: adminInfo.id,
        skapad_av_namn: adminNamn,
        avdelning: null,
        fran_datum: forstaDag,
        till_datum: forstaDag,
        status: 'beviljad',
        medarbetare_kommentar: null,
        admin_kommentar: `Dina passändringar för ${manadNamnText} är inlagda i SAP. Kontrollera ditt arbetsschema så att allt stämmer.`,
        besvarad_av: adminInfo.id,
        besvarad_av_namn: adminNamn,
        besvarad_at: nu,
        mottagare_id: medId,
        passandring_fran: 'SAP klar',
        passandring_till: manadNamnText,
        passandring_datum: forstaDag
      })
      if (notisError) {
        // Notisen gick inte att skicka: ta tillbaka märkningen så man kan försöka igen
        await supabase.from('sap_klar').delete().eq('medarbetare_id', medId).eq('manad', valdManad)
        throw notisError
      }
      await hamtaKlar()
    } catch (err) {
      console.error('Klar i SAP:', err)
      setKlarFel('Kunde inte skicka notisen: ' + (err?.message || 'okänt fel'))
    } finally {
      setKlarSkickar(false)
    }
  }
  useEffect(() => { if (flik === 'semester') hamtaSemester() }, [flik, semAr, valdAvdelning])

  // Beviljad semester under semesteråret (1/4 - 31/3), hämtas i omgångar p.g.a. 1000-radsgränsen
  async function hamtaSemester() {
    const nyckel = aktuellNyckel.current
    setLaddarSem(true)
    const fran = `${semAr}-04-01`
    const till = `${semAr + 1}-03-31`
    let ids = null
    if (valdAvdelning !== 'Alla') {
      const { data: medarbetareIAvd } = await supabase.from('medarbetare').select('id').eq('avdelning', valdAvdelning)
      ids = medarbetareIAvd?.map(m => m.id) || []
    }
    let alla = []
    for (let start = 0; ; start += 1000) {
      let q = supabase
        .from('pass')
        .select('id, datum, passtyp_namn, arbetstimmar, kategori, gul_nytt, gul_ursprung_namn, medarbetare:medarbetare(id, fornamn, efternamn, avdelning, anstallningsnummer)')
        .eq('kategori', 'beviljad_semester')
        .gte('datum', fran)
        .lte('datum', till)
        .order('datum', { ascending: true })
        .order('id', { ascending: true })
        .range(start, start + 999)
      if (ids) q = q.in('medarbetare_id', ids)
      const { data } = await q
      if (!data || data.length === 0) break
      alla = alla.concat(data)
      if (data.length < 1000) break
    }
    if (nyckel !== aktuellNyckel.current) return // gammalt svar, ett nyare val gäller
    setSemLista(alla)
    setLaddarSem(false)
  }

  // Typ av semesterdag: 'V' veckovila, 'X' X-dag, annars vanlig. null = dag som var ledig från början (räknas inte)
  function semesterTyp(p) {
    const namn = (p.passtyp_namn || '').toUpperCase().trim()
    const bas = namn.startsWith('S') ? namn.slice(1) : namn
    if (p.gul_nytt || bas === '' || bas === 'S') return null
    if (bas === 'V') return 'V'
    if (bas === 'X') return 'X'
    return 'vanlig'
  }

  const semPerMedarbetare = {}
  for (const p of semLista) {
    const typ = semesterTyp(p)
    if (!typ) continue
    const namn = `${p.medarbetare?.fornamn} ${p.medarbetare?.efternamn}`
    if (!semPerMedarbetare[namn]) semPerMedarbetare[namn] = { avdelning: p.medarbetare?.avdelning, anstallningsnummer: p.medarbetare?.anstallningsnummer, dagar: [] }
    semPerMedarbetare[namn].dagar.push({ ...p, _typ: typ })
  }

  async function hamtaAndrade() {
    const nyckel = aktuellNyckel.current
    setLoading(true)
    const [ar, manad] = valdManad.split('-').map(Number)
    const fran = `${ar}-${String(manad).padStart(2,'0')}-01`
    const slut = new Date(ar, manad, 0)
    const till = `${ar}-${String(manad).padStart(2,'0')}-${String(slut.getDate()).padStart(2,'0')}`

    let query = supabase
      .from('pass')
      .select('*, medarbetare:medarbetare(id, fornamn, efternamn, avdelning, anstallningsnummer), passtyp:passtyper(namn, starttid, sluttid)')
      .eq('har_passandring', true)
      .gte('datum', fran)
      .lte('datum', till)
      .order('datum', { ascending: true })
      .limit(5000)

    if (valdAvdelning !== 'Alla') {
      const { data: medarbetareIAvd } = await supabase
        .from('medarbetare')
        .select('id')
        .eq('avdelning', valdAvdelning)
      const ids = medarbetareIAvd?.map(m => m.id) || []
      query = query.in('medarbetare_id', ids)
    }

    const { data } = await query
    const passLista = data || []

    // Hämta all ändringshistorik per pass (behöver både senaste ändringsdatum och
    // det UR­SPRUNGLIGA passet för att räkna ut timmar utanför planerad tid)
    const passIds = passLista.map(p => p.id)
    let historikPerPass = {}
    if (passIds.length > 0) {
      const { data: historikData } = await supabase
        .from('pass_historik')
        .select('pass_id, andrad_at, gammal_passtyp, ny_passtyp, gammal_kategori, ny_kategori')
        .in('pass_id', passIds)
        .order('andrad_at', { ascending: true })
      for (const h of historikData || []) {
        // Hoppa över rena ATF-loggar (samma passtyp/kategori före och efter)
        const arRiktigAndring = h.gammal_passtyp !== h.ny_passtyp || h.gammal_kategori !== h.ny_kategori
        if (!arRiktigAndring) continue
        if (!historikPerPass[h.pass_id]) {
          historikPerPass[h.pass_id] = { senasteGammalPasstyp: h.gammal_passtyp, senasteGammalKategori: h.gammal_kategori, senasteAndradAt: h.andrad_at }
        } else {
          // ascending ordning → sista skrivning = senaste ändringen
          historikPerPass[h.pass_id].senasteGammalPasstyp = h.gammal_passtyp
          historikPerPass[h.pass_id].senasteGammalKategori = h.gammal_kategori
          historikPerPass[h.pass_id].senasteAndradAt = h.andrad_at
        }
      }
    }

    // Hämta tider för alla passtyper, så vi kan slå upp det ursprungliga passets tid via namnet
    const { data: passtyperData } = await supabase.from('passtyper').select('namn, starttid, sluttid')
    const passtyperMap = {}
    for (const pt of passtyperData || []) passtyperMap[pt.namn] = pt

    const passMedErsattning = passLista.map(p => {
      const info = historikPerPass[p.id]
      const gammalTider = info?.senasteGammalPasstyp ? passtyperMap[basPassNamn(info.senasteGammalPasstyp)] : null
      return {
        ...p,
        _andradAt: info?.senasteAndradAt || null,
        _foreDetta: info?.senasteGammalKategori || null,
        _registreradeTimmar: (gammalTider && p.passtyp)
          ? beraknaRegistreradeTimmar(gammalTider.starttid, gammalTider.sluttid, p.passtyp.starttid, p.passtyp.sluttid)
          : null
      }
    })

    // Pass som ger skiftersättning men där inga timmar ligger utanför det ursprungliga
    // passets tid (t.ex. samma tider) ska inte med i underlaget - ingen OB-ersättning.
    const utanErsattningKat = ['passbyte','overtid','semester','beviljad_semester','foraldraledighet','beviljad_foraldraledighet','permission','flex','sjukdom','ledig']
    const synliga = passMedErsattning.filter(p =>
      utanErsattningKat.includes(p.kategori) || p._registreradeTimmar !== 0
    )

    if (nyckel !== aktuellNyckel.current) return // gammalt svar, ett nyare val gäller
    setAndrade(synliga)
    setLoading(false)
  }

  async function hamtaAtf() {
    const nyckel = aktuellNyckel.current
    const [ar, manad] = valdManad.split('-').map(Number)
    const fran = `${ar}-${String(manad).padStart(2,'0')}-01`
    const slut = new Date(ar, manad, 0)
    const till = `${ar}-${String(manad).padStart(2,'0')}-${String(slut.getDate()).padStart(2,'0')}`

    let query = supabase
      .from('pass')
      .select('*, medarbetare:medarbetare(id, fornamn, efternamn, avdelning, anstallningsnummer)')
      .eq('har_atf', true)
      .gte('datum', fran)
      .lte('datum', till)
      .order('datum', { ascending: true })
      .limit(5000)

    if (valdAvdelning !== 'Alla') {
      const { data: medarbetareIAvd } = await supabase
        .from('medarbetare')
        .select('id')
        .eq('avdelning', valdAvdelning)
      const ids = medarbetareIAvd?.map(m => m.id) || []
      query = query.in('medarbetare_id', ids)
    }

    const { data } = await query
    if (nyckel !== aktuellNyckel.current) return // gammalt svar, ett nyare val gäller
    setAtfLista(data || [])
  }

  function atfTimmar(p) {
    if (!p.atf_fran || !p.atf_till) return null
    const [h1, m1] = p.atf_fran.split(':').map(Number)
    const [h2, m2] = p.atf_till.split(':').map(Number)
    let min = (h2 * 60 + m2) - (h1 * 60 + m1)
    if (min < 0) min += 1440
    return Math.round((min / 60) * 100) / 100
  }

  // Gruppera ATF per medarbetare
  const atfPerMedarbetare = atfLista.reduce((acc, p) => {
    const namn = `${p.medarbetare?.fornamn} ${p.medarbetare?.efternamn}`
    if (!acc[namn]) acc[namn] = { avdelning: p.medarbetare?.avdelning, anstallningsnummer: p.medarbetare?.anstallningsnummer, pass: [] }
    acc[namn].pass.push(p)
    return acc
  }, {})

  function formateraDatum(datum) {
    return new Date(datum + 'T12:00:00').toLocaleDateString('sv-SE', {
      weekday: 'short', day: 'numeric', month: 'short'
    })
  }

  // Gruppera per medarbetare
  const perMedarbetare = andrade.reduce((acc, p) => {
    const namn = `${p.medarbetare?.fornamn} ${p.medarbetare?.efternamn}`
    if (!acc[namn]) acc[namn] = { id: p.medarbetare?.id, avdelning: p.medarbetare?.avdelning, anstallningsnummer: p.medarbetare?.anstallningsnummer, pass: [] }
    acc[namn].pass.push(p)
    return acc
  }, {})

  return (
    <div className="sap-page-root" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
      <div className="content-header">
        <div>
          <h1>SAP-underlag</h1>
          <div className="content-sub">{flik === 'passandringar' ? 'Passändringar markerade med ⭐' : flik === 'semester' ? 'Beviljad semester under semesteråret' : 'Registrerad ATF'}</div>
        </div>
        <div className="no-print" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 4, background: '#f2f2f2', borderRadius: 6, padding: 3 }}>
            <button
              className={`btn btn-sm ${flik === 'passandringar' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ boxShadow: 'none' }}
              onClick={() => setFlik('passandringar')}
            >Passändringar</button>
            <button
              className={`btn btn-sm ${flik === 'atf' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ boxShadow: 'none' }}
              onClick={() => setFlik('atf')}
            >ATF</button>
            <button
              className={`btn btn-sm ${flik === 'semester' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ boxShadow: 'none' }}
              onClick={() => setFlik('semester')}
            >Semester</button>
          </div>
          {flik === 'semester' ? (
            <select
              className="form-control"
              style={{ margin: 0, width: 'auto' }}
              value={semAr}
              onChange={e => setSemAr(Number(e.target.value))}
            >
              {Array.from({ length: Math.max(1, new Date().getFullYear() + 1 - 2026 + 1) }, (_, i) => 2026 + i).map(ar => (
                <option key={ar} value={ar}>1/4 {ar} – 31/3 {ar + 1}</option>
              ))}
            </select>
          ) : (
            <input
              type="month"
              className="form-control"
              style={{ margin: 0, width: 'auto' }}
              value={valdManad}
              onChange={e => setValdManad(e.target.value)}
            />
          )}
          <select
            className="form-control"
            style={{ margin: 0, width: 'auto' }}
            value={valdAvdelning}
            onChange={e => setValdAvdelning(e.target.value)}
          >
            {AVDELNINGAR.map(a => <option key={a}>{a}</option>)}
          </select>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => window.print()}
          >
            🖨 Skriv ut
          </button>
        </div>
      </div>

      <div className="content-body sap-print-area" style={{ overflowY: 'auto' }}>
        {flik === 'semester' ? (
          laddarSem ? (
            <div className="spinner" style={{ margin: '40px auto' }} />
          ) : Object.keys(semPerMedarbetare).length === 0 ? (
            <div className="card">
              <p style={{ color: '#aaa', textAlign: 'center' }}>Ingen beviljad semester för vald avdelning och semesterår</p>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 16, fontSize: '.82rem', color: '#888' }}>
                Observera att appen inte kan se om medarbetaren har registrerat dagarna som semesterdagar eller som ATF-timmar. Detta är bara en sammanställning 😊
              </div>
              {Object.entries(semPerMedarbetare).map(([namn, info]) => {
                const antalV = info.dagar.filter(d => d._typ === 'V').length
                const antalX = info.dagar.filter(d => d._typ === 'X').length
                const varav = [antalV > 0 && `${antalV} veckovila`, antalX > 0 && `${antalX} X-${antalX === 1 ? 'dag' : 'dagar'}`].filter(Boolean).join(' och ')
                return (
                  <div key={namn} style={{ marginBottom: 20 }}>
                    <div style={{
                      fontWeight: 700, fontSize: '.85rem', padding: '6px 12px',
                      background: '#f5f5f5', borderRadius: '6px 6px 0 0',
                      borderBottom: '2px solid #2e7d32', display: 'flex', justifyContent: 'space-between'
                    }}>
                      <span>{namn} – anställningsnummer: {info.anstallningsnummer || '–'}</span>
                      <span style={{ fontWeight: 400, color: '#888', fontSize: '.78rem' }}>{info.avdelning}</span>
                    </div>
                    <div style={{ padding: '8px 12px', fontWeight: 700, fontSize: '.85rem' }}>
                      Semesterdagar: {info.dagar.length}{varav && <span style={{ fontWeight: 400, color: '#777' }}> (varav {varav})</span>}
                    </div>
                    <div style={{ padding: '0 12px 8px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {info.dagar.map(d => (
                        <span key={d.id} style={{
                          fontSize: '.76rem', padding: '3px 8px', borderRadius: 12,
                          background: d._typ === 'vanlig' ? '#e8f5e9' : '#eeeeee',
                          color: d._typ === 'vanlig' ? '#2e7d32' : '#888'
                        }}>
                          {formateraDatum(d.datum)}{d._typ === 'V' ? ' · veckovila' : d._typ === 'X' ? ' · X-dag' : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              })}
            </>
          )
        ) : flik === 'passandringar' ? (
        loading ? (
          <div className="spinner" style={{ margin: '40px auto' }} />
        ) : andrade.length === 0 ? (
          <div className="card">
            <p style={{ color: '#aaa', textAlign: 'center' }}>
              Inga passändringar för vald månad och avdelning
            </p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16, fontSize: '.82rem', color: '#888' }}>
              {andrade.length} passändringar · {Object.keys(perMedarbetare).length} medarbetare
            </div>
            <div className="no-print" style={{ marginBottom: 12, fontSize: '.76rem', color: '#888', fontStyle: 'italic' }}>
              "Klar i SAP" skickar en notis till medarbetaren i appen om att månadens passändringar är inlagda i SAP och att hen ska kontrollera sitt arbetsschema.
            </div>
            {klarFel && (
              <div className="info-box info-box-yellow no-print" style={{ fontSize: '.78rem', marginBottom: 12 }}>{klarFel}</div>
            )}

            {Object.entries(perMedarbetare).map(([namn, info]) => (
              <div key={namn} style={{ marginBottom: 20 }}>
                <div style={{
                  fontWeight: 700, fontSize: '.85rem', padding: '6px 12px',
                  background: '#f5f5f5', borderRadius: '6px 6px 0 0',
                  borderBottom: '2px solid #F11C0A', display: 'flex', justifyContent: 'space-between'
                }}>
                  <span>{namn} – anställningsnummer: {info.anstallningsnummer || '–'}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 400, color: '#888', fontSize: '.78rem' }}>{info.avdelning}</span>
                    {klarMap[info.id] ? (
                      <span className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 600, color: '#2e7d32', fontSize: '.75rem' }}>
                          ✓ Klar i SAP · skickad {new Date(klarMap[info.id].skickad_at).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}
                        </span>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={klarSkickar}
                          onClick={() => { setKlarFel(''); setAngraFraga({ id: info.id, namn }) }}
                          style={{ fontSize: '.7rem', padding: '2px 8px' }}
                        >Ångra</button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm no-print"
                        disabled={klarSkickar || !info.id}
                        onClick={() => { setKlarFel(''); setKlarFraga({ id: info.id, namn }) }}
                        style={{ fontSize: '.72rem', padding: '3px 10px' }}
                      >Klar i SAP</button>
                    )}
                  </span>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem', tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '14%' }} />
                  </colgroup>
                  <thead>
                    <tr style={{ background: '#fafafa' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: '#888', fontWeight: 600, borderBottom: '1px solid #eee' }}>Datum</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: '#888', fontWeight: 600, borderBottom: '1px solid #eee' }}>Pass</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: '#888', fontWeight: 600, borderBottom: '1px solid #eee' }}>Tid</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', color: '#888', fontWeight: 600, borderBottom: '1px solid #eee' }}>Timmar</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: '#888', fontWeight: 600, borderBottom: '1px solid #eee' }}>Kategori</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: '#888', fontWeight: 600, borderBottom: '1px solid #eee' }}>Skiftersättning</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', color: '#888', fontWeight: 600, borderBottom: '1px solid #eee' }}>Timmar att registrera</th>
                    </tr>
                  </thead>
                  <tbody>
                    {info.pass.map(p => {
                      const kategoriUtanErsattning = ['passbyte','overtid','semester','beviljad_semester','foraldraledighet','beviljad_foraldraledighet','permission','flex','sjukdom','ledig']
                      const ers = !kategoriUtanErsattning.includes(p.kategori) ? beraknaSkiftersattning(p._andradAt, p.datum) : null
                      return (
                      <tr key={p.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{formateraDatum(p.datum)}</td>
                        <td style={{ padding: '8px 12px' }}>
                          <span style={{ fontWeight: 700 }}>⭐ {p.passtyp_namn || '–'}</span>
                        </td>
                        <td style={{ padding: '8px 12px', color: '#666' }}>
                          {p.passtyp?.starttid?.substring(0,5)}–{p.passtyp?.sluttid?.substring(0,5) || '–'}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>
                          {p.arbetstimmar > 0 ? `${p.arbetstimmar}h` : '–'}
                        </td>
                        <td style={{ padding: '8px 12px', color: '#888', fontSize: '.75rem' }}>
                          {p.kategori === 'passbyte' ? 'passbyte' :
                           p.kategori === 'beredskap' ? 'beredskap' :
                           p.kategori === 'sjukdom' ? 'sjukdom' :
                           p.kategori === 'boka_dag' ? 'boka dag' :
                           p.kategori === 'ledig' ? 'betald ledig dag' :
                           p.kategori === 'overtid' ? 'övertid' :
                           p.kategori ? p.kategori.charAt(0).toLowerCase() + p.kategori.slice(1) : '–'}
                          {p._foreDetta && p._foreDetta !== p.kategori && (
                            <div style={{ fontSize: '.68rem', color: '#bbb', fontStyle: 'italic' }}>tidigare: {p._foreDetta}</div>
                          )}
                        </td>
                        <td style={{ padding: '8px 12px', fontSize: '.75rem', fontWeight: 700, color: '#e65100' }}>
                          {ers ? ers.text : '–'}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>
                          {(!ers || ers.divisor === null || p._registreradeTimmar === null) ? '–' : `${p._registreradeTimmar}h`}
                        </td>
                      </tr>
                      )
                    })}
                    <tr style={{ background: '#fafafa', fontWeight: 700 }}>
                      <td colSpan={3} style={{ padding: '8px 12px', color: '#888', fontSize: '.78rem' }}>
                        Totalt {info.pass.length} passändringar
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                        {info.pass.reduce((s, p) => s + (p.arbetstimmar || 0), 0).toFixed(1)}h
                      </td>
                      <td />
                      <td />
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
          </>
        )
        ) : (
          atfLista.length === 0 ? (
            <div className="card">
              <p style={{ color: '#aaa', textAlign: 'center' }}>
                Ingen registrerad ATF för vald månad och avdelning
              </p>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 16, fontSize: '.82rem', color: '#888' }}>
                {atfLista.length} registrerade ATF-tillfällen · {Object.keys(atfPerMedarbetare).length} medarbetare
              </div>

              {Object.entries(atfPerMedarbetare).map(([namn, info]) => (
                <div key={namn} style={{ marginBottom: 20 }}>
                  <div style={{
                    fontWeight: 700, fontSize: '.85rem', padding: '6px 12px',
                    background: '#f5f5f5', borderRadius: '6px 6px 0 0',
                    borderBottom: '2px solid #1565c0', display: 'flex', justifyContent: 'space-between'
                  }}>
                    <span>{namn} – anställningsnummer: {info.anstallningsnummer || '–'}</span>
                    <span style={{ fontWeight: 400, color: '#888', fontSize: '.78rem' }}>{info.avdelning}</span>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem' }}>
                    <thead>
                      <tr style={{ background: '#fafafa' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', color: '#888', fontWeight: 600, borderBottom: '1px solid #eee' }}>Datum</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', color: '#888', fontWeight: 600, borderBottom: '1px solid #eee' }}>Pass</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', color: '#888', fontWeight: 600, borderBottom: '1px solid #eee' }}>ATF från–till</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', color: '#888', fontWeight: 600, borderBottom: '1px solid #eee' }}>Timmar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {info.pass.map(p => (
                        <tr key={p.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                          <td style={{ padding: '8px 12px', fontWeight: 500 }}>{formateraDatum(p.datum)}</td>
                          <td style={{ padding: '8px 12px' }}>{p.passtyp_namn || '–'}</td>
                          <td style={{ padding: '8px 12px', color: '#0d47a1', fontWeight: 700 }}>
                            {p.atf_fran?.substring(0,5)}–{p.atf_till?.substring(0,5)}
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>
                            {atfTimmar(p) !== null ? `${atfTimmar(p)}h` : '–'}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ background: '#fafafa', fontWeight: 700 }}>
                        <td colSpan={3} style={{ padding: '8px 12px', color: '#888', fontSize: '.78rem' }}>
                          Totalt {info.pass.length} ATF-tillfällen
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                          {info.pass.reduce((s, p) => s + (atfTimmar(p) || 0), 0).toFixed(1)}h
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ))}
            </>
          )
        )}
      </div>
      {angraFraga && (
        <BekraftaModal
          titel="Ångra Klar i SAP"
          meddelande={`Ta bort märket och notisen för ${angraFraga.namn}? Notisen försvinner ur inkorgen. Har ${angraFraga.namn} redan sett notisen eller fått en push går det inte att ta tillbaka.`}
          onBekrafta={() => { const f = angraFraga; setAngraFraga(null); angraKlar(f.id) }}
          onAvbryt={() => setAngraFraga(null)}
        />
      )}
      {klarFraga && (
        <BekraftaModal
          titel="Klar i SAP"
          meddelande={`Skicka en notis till ${klarFraga.namn} om att passändringarna för ${manadNamnText} är inlagda i SAP? Du kan ångra det efteråt.`}
          onBekrafta={() => { const f = klarFraga; setKlarFraga(null); skickaKlar(f.id) }}
          onAvbryt={() => setKlarFraga(null)}
        />
      )}
    </div>
  )
}
